import React, { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { centralService } from "../../../services/centralService";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import Button from "../../ui/Button";
import {
  CENTRAL_PROJECT_TYPES,
  DONER_SCHEMES,
  DEFAULT_CENTRAL_PROJECT_DATA,
} from "../../../constants/centralConstants";
import { validateCentralProjectForm } from "../../../utils/centralValidation";
import { financialYearUtils } from "../../../utils/helpers";

const DonerForm = () => {
  const { user } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();

  const [formData, setFormData] = useState({
    ...DEFAULT_CENTRAL_PROJECT_DATA,
    type: CENTRAL_PROJECT_TYPES.DONER,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  // Responsive configurations
  const titleSize = getResponsiveValue({
    mobile: "text-xl",
    desktop: "text-2xl",
  });

  const gridCols2 = getResponsiveValue({
    mobile: "grid-cols-1",
    desktop: "grid-cols-2",
  });

  const gridCols3 = getResponsiveValue({
    mobile: "grid-cols-1",
    tablet: "grid-cols-2",
    desktop: "grid-cols-3",
  });

  const gridGap = getResponsiveValue({
    mobile: "gap-4",
    desktop: "gap-6",
  });

  // Auto-calculate total when amounts change
  useEffect(() => {
    const centralAmount = parseFloat(formData.centralShare) || 0;
    const stateAmount = parseFloat(formData.stateShare) || 0;
    const total = centralAmount + stateAmount;

    setFormData((prev) => ({
      ...prev,
      totalAmount: total,
    }));
  }, [formData.centralShare, formData.stateShare]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    if (success) setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateCentralProjectForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      const result = await centralService.submitCentralProject(
        formData,
        user.uid,
        CENTRAL_PROJECT_TYPES.DONER,
      );

      if (result.success) {
        setSuccess(true);
        setSubmitMessage("DoNER project submitted successfully!");

        // Reset form
        setFormData({
          ...DEFAULT_CENTRAL_PROJECT_DATA,
          type: CENTRAL_PROJECT_TYPES.DONER,
        });

        setTimeout(() => {
          setSuccess(false);
          setSubmitMessage("");
        }, 5000);
      }
    } catch (error) {
      console.error("Error submitting DoNER project:", error);
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const currentFinancialYear = financialYearUtils.getCurrentFinancialYear();
  const financialYearDescription =
    financialYearUtils.getFinancialYearDescription(currentFinancialYear);

  // Set initial financial year
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      financialYear: currentFinancialYear,
    }));
  }, [currentFinancialYear]);

  const schemeOptions = DONER_SCHEMES.map((scheme) => ({
    value: scheme.value,
    label: scheme.label,
  }));

  const financialYearOptions = financialYearUtils.getFinancialYearOptions();

  return (
    <div className="space-y-4">
      {/* Success Message */}
      {success && (
        <div className="bg-success-subtle border border-border-primary rounded-xl p-3">
          <div className="flex items-center">
            <svg
              className="w-4 h-4 text-success mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-success font-medium text-sm">
              {submitMessage}
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className={`${titleSize} font-bold text-text-primary mb-1`}>
          DoNER Projects
        </h1>
        <p className="text-text-secondary text-sm">
          Submit a new DoNER project proposal
        </p>
      </div>

      {/* Form */}
      <div>
        <div>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error */}
            {errors.general && (
              <div className="bg-error-subtle border border-border-error rounded-xl p-3">
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 text-error mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-error text-sm">{errors.general}</span>
                </div>
              </div>
            )}

            {/* Basic Project Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary border-b border-border-primary pb-2">
                Basic Project Information
              </h3>

              {/* Financial Year and Scheme Selection - Two columns */}
              <div className={`grid ${gridCols2} ${gridGap}`}>
                <div>
                  <Select
                    label="Financial Year"
                    value={formData.financialYear}
                    onChange={(e) =>
                      handleInputChange("financialYear", e.target.value)
                    }
                    options={financialYearOptions}
                    error={errors.financialYear}
                    required
                  />
                </div>

                <div>
                  <Select
                    label="Scheme"
                    value={formData.scheme}
                    onChange={(e) =>
                      handleInputChange("scheme", e.target.value)
                    }
                    options={schemeOptions}
                    error={errors.scheme}
                    required
                    placeholder="Select Scheme"
                  />
                </div>
              </div>

              {/* Project Name */}
              <div>
                <Input
                  label="Name of Project"
                  type="text"
                  value={formData.projectName}
                  onChange={(e) =>
                    handleInputChange("projectName", e.target.value)
                  }
                  placeholder="Enter project name"
                  error={errors.projectName}
                  required
                />
              </div>
            </div>

            {/* Financial Details - Three columns */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary border-b border-border-primary pb-2">
                Financial Details (Rs. in lakh)
              </h3>

              <div className={`grid ${gridCols3} ${gridGap}`}>
                {/* DoNER Share */}
                <Input
                  label="DoNER Share"
                  type="number"
                  value={formData.centralShare}
                  onChange={(e) =>
                    handleInputChange("centralShare", e.target.value)
                  }
                  placeholder="Enter the amount"
                  error={errors.centralShare}
                  required
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                      />
                    </svg>
                  }
                />

                {/* State Share */}
                <Input
                  label="State Share"
                  type="number"
                  value={formData.stateShare}
                  onChange={(e) =>
                    handleInputChange("stateShare", e.target.value)
                  }
                  placeholder="Enter the amount"
                  error={errors.stateShare}
                  required
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                      />
                    </svg>
                  }
                />

                {/* Total - Read Only */}
                <Input
                  label="Total"
                  type="text"
                  value={formData.totalAmount.toFixed(2)}
                  readOnly
                  className="bg-gray-50"
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                      />
                    </svg>
                  }
                />
              </div>
            </div>

            {/* NEW: Work Order Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary border-b border-border-primary pb-2">
                Work Order Details
              </h3>

              <div className={`grid ${gridCols3} ${gridGap}`}>
                <Input
                  label="Date of Sanction"
                  type="text"
                  value={formData.workOrderNumber}
                  onChange={(e) =>
                    handleInputChange("workOrderNumber", e.target.value)
                  }
                  placeholder="Enter Date of Sanction"
                  error={errors.workOrderNumber}
                  required
                />

                <Input
                  label="Date of Issue of Work Order"
                  type="date"
                  value={formData.workOrderIssueDate}
                  onChange={(e) =>
                    handleInputChange("workOrderIssueDate", e.target.value)
                  }
                  error={errors.workOrderIssueDate}
                  required
                />

                <Input
                  label="Scheduled Date of Completion"
                  type="date"
                  value={formData.scheduledCompletionDate}
                  onChange={(e) =>
                    handleInputChange("scheduledCompletionDate", e.target.value)
                  }
                  error={errors.scheduledCompletionDate}
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-4 border-t border-border-primary">
              <Button
                type="submit"
                loading={loading}
                disabled={loading}
                size="md"
                className={isMobile ? "w-full" : ""}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DonerForm;
